import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import useLivePoll from "@/hooks/useLivePoll";
import LiveDot from "@/components/LiveDot";
import { Keyboard, AlertTriangle, Lock, Clock, Search } from "lucide-react";

const STATES = ["Draft", "In Transcription", "Ready for Review", "Final", "Amended"];

const BADGE = {
  "Draft": "bg-slate-100 text-slate-700",
  "In Transcription": "bg-sky-100 text-sky-900",
  "Ready for Review": "bg-amber-100 text-amber-900",
  "Final": "bg-emerald-100 text-emerald-900",
  "Amended": "bg-purple-100 text-purple-900",
};

export const StatusPill = ({ status }) => (
  <span className={`inline-flex items-center px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide ${BADGE[status] || BADGE.Draft}`}
        data-testid={`report-status-pill-${status.replace(/\s/g, "-")}`}>
    {status}
  </span>
);

export default function Transcription() {
  const { user } = useAuth();
  const [data, setData] = useState({ cases: [], counts: {}, tat_target_hours: 48 });
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("");
  const [scope, setScope] = useState("pending");

  const load = useCallback(async (silent = false) => {
    try {
      const res = await api.get("/transcription/queue", { params: { q, report_status: filter, scope } });
      setData(res.data);
    } catch (e) { if (!silent) toast.error(apiError(e)); }
  }, [q, filter, scope]);

  useEffect(() => { load(); }, [load]);
  const lastSync = useLivePoll(() => load(true), 10000);

  const rows = data.cases || [];
  const readyMine = rows.filter((r) => r.report_status === "Ready for Review"
    && (!r.pathologist_id || r.pathologist_id === user?.id)).length;

  return (
    <div className="space-y-6" data-testid="transcription-page">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#134e4a]">TraX Suite — Transcription</p>
        <h1 className="mt-1 flex items-center gap-2 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
          <Keyboard size={26} className="text-[#134e4a]" /> Transcription queue
        </h1>
        <p className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-600">
          {rows.length} report{rows.length === 1 ? "" : "s"} in the queue · STAT first, then oldest received.
          TAT target {data.tat_target_hours}h — the clock runs until sign-out.
          <LiveDot lastSync={lastSync} testid="transcription-live-indicator" />
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid="transcription-filters">
        <div className="relative w-full sm:w-72">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="transcription-search"
                 placeholder="Accession, patient or MRN" className="h-10 pl-9" />
        </div>
        <div className="flex flex-wrap gap-1">
          <button onClick={() => setFilter("")} data-testid="filter-all"
                  className={`h-10 border px-3 text-sm font-medium ${filter === "" ? "border-[#134e4a] bg-[#134e4a] text-white" : "border-slate-300 bg-white text-slate-700 hover:border-[#134e4a]"}`}>
            All
          </button>
          {STATES.map((s) => (
            <button key={s} onClick={() => setFilter(s)} data-testid={`filter-${s.replace(/\s/g, "-")}`}
                    className={`h-10 border px-3 text-sm font-medium ${filter === s ? "border-[#134e4a] bg-[#134e4a] text-white" : "border-slate-300 bg-white text-slate-700 hover:border-[#134e4a]"}`}>
              {s}{data.counts?.[s] ? ` (${data.counts[s]})` : ""}
            </button>
          ))}
        </div>
        <select value={scope} onChange={(e) => setScope(e.target.value)} data-testid="transcription-scope"
                className="h-10 border border-slate-300 bg-white px-2 text-sm text-slate-700">
          <option value="pending">Open reports</option>
          <option value="signed">Signed out</option>
          <option value="all">Everything</option>
          {user?.role === "pathologist" && <option value="mine">Assigned to me</option>}
        </select>
      </div>

      {readyMine > 0 && (
        <div className="flex items-center gap-2 border-l-4 border-amber-500 bg-amber-50 px-3 py-2 text-sm text-amber-900"
             data-testid="transcription-ready-banner">
          <AlertTriangle size={15} /> {readyMine} report{readyMine === 1 ? "" : "s"} flagged ready for pathologist review.
        </div>
      )}

      <section className="border border-slate-200" data-testid="transcription-queue">
        {rows.length === 0 ? (
          <p className="px-3 py-10 text-center text-sm text-slate-500" data-testid="transcription-empty">
            Nothing in the queue.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[980px] text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr className="border-b border-slate-200">
                  <th className="px-3 py-2">Case</th>
                  <th className="px-3 py-2">Patient</th>
                  <th className="px-3 py-2">Specimen</th>
                  <th className="px-3 py-2">Pathologist</th>
                  <th className="px-3 py-2">Report status</th>
                  <th className="px-3 py-2">Priority</th>
                  <th className="px-3 py-2">Received</th>
                  <th className="px-3 py-2 text-right">TAT</th>
                  <th className="px-3 py-2 text-right">Open</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-slate-100 row-hover hover:bg-slate-50"
                      data-testid={`transcription-row-${r.accession_number}`}>
                    <td className="px-3 py-2">
                      <Link to={`/transcription/${r.id}`} className="font-mono font-semibold text-[#134e4a] hover:underline">
                        {r.accession_number}
                      </Link>
                    </td>
                    <td className="px-3 py-2 text-slate-800">{r.patient_name}</td>
                    <td className="px-3 py-2 text-slate-700">{r.specimen_type || "—"}</td>
                    <td className="px-3 py-2 text-slate-700">{r.pathologist_name || <span className="text-slate-400">Unassigned</span>}</td>
                    <td className="px-3 py-2">
                      <StatusPill status={r.report_status} />
                      {r.locked && <Lock size={12} className="ml-1 inline text-slate-400" />}
                    </td>
                    <td className="px-3 py-2">
                      {r.priority === "STAT"
                        ? <span className="bg-red-100 px-2 py-0.5 text-[11px] font-bold uppercase text-red-800">STAT</span>
                        : <span className="text-slate-500">Routine</span>}
                    </td>
                    <td className="px-3 py-2 text-slate-600">{fmtDateTime(r.received_at)}</td>
                    <td className={`num px-3 py-2 text-right font-semibold ${r.tat_breached ? "text-red-700" : "text-slate-700"}`}>
                      <span className="inline-flex items-center gap-1">
                        {r.tat_running && <Clock size={12} className={r.tat_breached ? "text-red-600" : "text-slate-400"} />}
                        {r.tat_hours}h
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Link to={`/transcription/${r.id}`} data-testid={`open-report-${r.accession_number}`}
                            className="inline-flex h-9 items-center border border-slate-300 px-3 text-xs font-semibold text-[#134e4a] hover:border-[#134e4a]">
                        {r.locked ? "View" : "Transcribe"}
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
